import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Drawer, Skeleton, Space, Tooltip, Typography } from 'antd';
import { EditOutlined, WarningOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { StatusPill } from '@/components/StatusPill';
import { getModelAdoption, type AdoptionResponse, type ModelConflict, type ModelRow } from '@/api/models';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// The answer to "the grid shows 2 — what does that MEAN?"
//
// Every sentence rendered here is composed server-side by ModelRegistryRules and
// arrives on the row as `explanations` / `conflicts`. This component decides
// LAYOUT and EMPHASIS only. If a reading is wrong, it is wrong in one place, for
// every consumer, and gets fixed there.
//
// Layout puts problems first, then the readings, then usage — because an operator
// opening a row is far more often asking "is this right?" than "how busy is it?".
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  row: ModelRow | null;
  open: boolean;
  onClose: () => void;
  onEdit: (row: ModelRow) => void;
}

export const ModelDetailDrawer = observer(function ModelDetailDrawer({ row, open, onClose, onEdit }: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [adoption, setAdoption] = useState<AdoptionResponse | null>(null);
  const [loadingAdoption, setLoadingAdoption] = useState(false);

  useEffect(() => {
    if (!open || !row) {
      setAdoption(null);
      return;
    }
    let cancelled = false;
    setLoadingAdoption(true);
    getModelAdoption(row.id)
      .then((a) => {
        if (!cancelled) setAdoption(a);
      })
      .catch(() => {
        // Usage history is supporting detail — a failure here must not block the
        // readings, which are the reason the drawer exists.
        if (!cancelled) setAdoption(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingAdoption(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, row?.id]);

  if (!row) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={620}
      destroyOnClose
      styles={{ body: { padding: '18px 22px 28px' } }}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{row.name}</span>
          <StatusPill tone="slate">
            {row.merchantId == null ? 'Every tenant' : `Merchant ${row.merchantId}`}
          </StatusPill>
          {row.active ? <StatusPill tone="green">Active</StatusPill> : <StatusPill tone="slate">Off</StatusPill>}
        </span>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace" }}>
            ai.AIModel · Id {row.id}
          </Text>
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button type="primary" icon={<EditOutlined />} onClick={() => onEdit(row)}>
              Edit this deployment
            </Button>
          </Space>
        </div>
      }
    >
      {row.conflicts.length > 0 && (
        <Section title="Worth a look" isDark={isDark}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {row.conflicts.map((c) => (
              <ConflictCard key={c.code} conflict={c} isDark={isDark} />
            ))}
          </div>
        </Section>
      )}

      <Section
        title="What every setting means"
        hint="Read from this row's stored values. The literal cell contents are on the right, so you can reconcile against the table."
        isDark={isDark}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {row.explanations.map((e) => (
            <div
              key={e.field}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 14,
                alignItems: 'start',
                padding: '11px 0',
                borderBottom: `1px solid ${isDark ? colors.slate[800] : colors.slate[100]}`,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.07em',
                    color: colors.slate[400],
                    marginBottom: 3,
                  }}
                >
                  {e.label}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    lineHeight: 1.45,
                    color: isDark ? colors.slate[100] : colors.slate[900],
                  }}
                >
                  {e.reading}
                </div>
                {e.consequence && (
                  <div
                    style={{
                      fontSize: 12,
                      lineHeight: 1.5,
                      color: isDark ? colors.slate[400] : colors.slate[500],
                      marginTop: 4,
                    }}
                  >
                    {e.consequence}
                  </div>
                )}
              </div>
              <Tooltip title={`Stored in ${e.field}`}>
                <code
                  style={{
                    fontSize: 10.5,
                    padding: '3px 7px',
                    borderRadius: 5,
                    whiteSpace: 'nowrap',
                    maxWidth: 190,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    background: isDark ? colors.slate[900] : colors.slate[100],
                    color: isDark ? colors.slate[400] : colors.slate[500],
                  }}
                >
                  {e.raw}
                </code>
              </Tooltip>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Who uses it"
        hint="Two different questions. Agents wired is configured intent (ai.AiAgent.ModelId); calls is what actually ran."
        isDark={isDark}
      >
        <div style={{ display: 'flex', gap: 28, marginBottom: 14 }}>
          <Stat
            label="agents wired"
            value={row.agentsWired}
            hint={
              row.agentsWired > 0
                ? `${row.agentsWiredActive.toLocaleString()} of them active`
                : 'no agent points here'
            }
            isDark={isDark}
          />
          <Stat
            label="calls · 30 days"
            value={row.traffic30d}
            hint={
              row.traffic30d > 0 && row.agentsWired === 0
                ? 'traffic with no agent wired — reached another way'
                : undefined
            }
            isDark={isDark}
          />
        </div>

        {loadingAdoption ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : adoption && adoption.total > 0 ? (
          <DayBars days={adoption.byDay} isDark={isDark} />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            No calls recorded against this deployment in the last 30 days.
          </Text>
        )}
      </Section>
    </Drawer>
  );
});

// ── pieces ─────────────────────────────────────────────────────────────────

function ConflictCard({ conflict, isDark }: { conflict: ModelConflict; isDark: boolean }) {
  const tone =
    conflict.severity === 'Blocking' ? colors.red : conflict.severity === 'Silent' ? colors.amber : colors.slate;
  const label =
    conflict.severity === 'Blocking'
      ? 'Calls will fail'
      : conflict.severity === 'Silent'
        ? 'Setting is ignored'
        : 'Worth knowing';

  return (
    <div
      style={{
        padding: '11px 13px',
        borderRadius: 9,
        background: isDark ? `${tone[700]}22` : tone[100],
        border: `1px solid ${tone[600]}44`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
        <WarningOutlined style={{ color: tone[600], fontSize: 13 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? colors.slate[100] : colors.slate[900] }}>
          {conflict.headline}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: tone[600],
          }}
        >
          {label}
        </span>
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.55, color: isDark ? colors.slate[300] : colors.slate[600] }}>
        {conflict.detail}
      </div>
      <div style={{ marginTop: 6 }}>
        <code style={{ fontSize: 10.5, color: colors.slate[400] }}>{conflict.field}</code>
      </div>
    </div>
  );
}

/** 30 daily bars. Zero days are drawn as a floor tick so a gap never reads as missing data. */
function DayBars({ days, isDark }: { days: { day: string; count: number }[]; isDark: boolean }) {
  const max = Math.max(...days.map((d) => d.count), 1);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 62 }}>
        {days.map((d) => (
          <Tooltip key={d.day} title={`${d.day} · ${d.count.toLocaleString()} calls`}>
            <div
              style={{
                flex: 1,
                height: `${Math.max(2, (d.count / max) * 100)}%`,
                minHeight: 2,
                borderRadius: 2,
                background: d.count === 0 ? (isDark ? colors.slate[700] : colors.slate[200]) : colors.ink[600],
              }}
            />
          </Tooltip>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
        <Text type="secondary" style={{ fontSize: 10.5 }}>
          {days[0]?.day}
        </Text>
        <Text type="secondary" style={{ fontSize: 10.5 }}>
          peak {max.toLocaleString()}/day
        </Text>
        <Text type="secondary" style={{ fontSize: 10.5 }}>
          {days[days.length - 1]?.day}
        </Text>
      </div>
    </div>
  );
}

function Section({
  title,
  hint,
  isDark,
  children,
}: {
  title: string;
  hint?: string;
  isDark: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginBottom: hint ? 4 : 10,
        }}
      >
        {title}
      </div>
      {hint && (
        <div style={{ fontSize: 11.5, color: colors.slate[400], marginBottom: 10, lineHeight: 1.5 }}>{hint}</div>
      )}
      {children}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  isDark,
}: {
  label: string;
  value: number;
  hint?: string;
  isDark: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
          color: value > 0 ? (isDark ? colors.slate[50] : colors.slate[900]) : colors.slate[400],
        }}
      >
        {value.toLocaleString()}
      </div>
      <div
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: colors.slate[400],
          marginTop: 2,
        }}
      >
        {label}
      </div>
      {hint && (
        <div style={{ fontSize: 11.5, color: isDark ? colors.slate[400] : colors.slate[500], marginTop: 3 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
